/**
 * PricelistsSettings — מחירונים
 *
 * Master-detail component for inventory_pricelists + inventory_pricelist_items.
 *
 * Pricelist types (Phase 13):
 *   • Percentage  (is_manual=false) — applies a % discount to product base price.
 *                  Supports group-level and product-level discount rules via
 *                  inventory_pricelist_items.
 *   • Manual      (is_manual=true)  — stores an explicit price per product in
 *                  inventory_product_prices. The detail panel is read-only here;
 *                  prices are set from the Product form.
 *
 * Master panel:
 *   • List all pricelists with type badge
 *   • Add / Edit / Delete pricelists
 *   • Click a pricelist row to open its detail panel
 *
 * Detail panel (slide-in from the right):
 *   • Percentage: shows discount rule items (Add / Edit / Delete)
 *   • Manual: info card explaining where to set per-product prices
 */

import { useEffect, useState, useCallback } from 'react';
import { Tags, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, ChevronLeft, Percent, Folder, Package, ListFilter, CircleCheck as CheckCircle2, SlidersHorizontal, Info } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { supabase } from '@/lib/supabase';
import type { ProductGroup } from './ProductGroupsSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Pricelist {
  id:                       string;
  company_id:               string;
  name:                     string;
  default_discount_percent: number;
  is_manual:                boolean;
  created_at:               string;
  updated_at:               string;
}

interface Product {
  id:   string;
  name: string;
  sku?: string | null;
}

interface PricelistItem {
  id:                string;
  pricelist_id:      string;
  company_id:        string;
  product_group_id:  string | null;
  product_id:        string | null;
  discount_percent:  number;
  // Joined labels
  group_name?:   string | null;
  product_name?: string | null;
}

interface Props {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPct(n: number) {
  return `${Number(n).toFixed(2).replace(/\.?0+$/, '')}%`;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function PricelistsSettings({ companyId, showToast }: Props) {

  // ── Master state ──────────────────────────────────────────────────────────
  const [lists,       setLists]       = useState<Pricelist[]>([]);
  const [listsLoad,   setListsLoad]   = useState(true);
  const [listsError,  setListsError]  = useState<string | null>(null);
  const [selected,    setSelected]    = useState<Pricelist | null>(null);

  // Pricelist modal
  const [plModalOpen,  setPlModalOpen]  = useState(false);
  const [plEditing,    setPlEditing]    = useState<Pricelist | null>(null);
  const [plName,       setPlName]       = useState('');
  const [plDiscount,   setPlDiscount]   = useState('0');
  const [plIsManual,   setPlIsManual]   = useState(false);
  const [plSaving,     setPlSaving]     = useState(false);
  const [plFormErr,    setPlFormErr]    = useState<string | null>(null);
  const [plDelTarget,  setPlDelTarget]  = useState<Pricelist | null>(null);
  const [plDeleting,   setPlDeleting]   = useState(false);

  // ── Detail state ──────────────────────────────────────────────────────────
  const [items,      setItems]      = useState<PricelistItem[]>([]);
  const [itemsLoad,  setItemsLoad]  = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  // Groups + products for dropdowns
  const [groups,   setGroups]   = useState<ProductGroup[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  // Item modal
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemEditing,   setItemEditing]   = useState<PricelistItem | null>(null);
  const [itemTarget,    setItemTarget]    = useState<'group' | 'product'>('group');
  const [itemGroupId,   setItemGroupId]   = useState('');
  const [itemProductId, setItemProductId] = useState('');
  const [itemDiscount,  setItemDiscount]  = useState('0');
  const [itemSaving,    setItemSaving]    = useState(false);
  const [itemFormErr,   setItemFormErr]   = useState<string | null>(null);
  const [itemDelTarget, setItemDelTarget] = useState<PricelistItem | null>(null);
  const [itemDeleting,  setItemDeleting]  = useState(false);

  // ── Fetch pricelists ──────────────────────────────────────────────────────

  const fetchLists = useCallback(async () => {
    setListsLoad(true); setListsError(null);
    const { data, error } = await supabase
      .from('inventory_pricelists')
      .select('*')
      .eq('company_id', companyId)
      .order('name');
    if (error) setListsError(error.message);
    else       setLists((data as Pricelist[]) ?? []);
    setListsLoad(false);
  }, [companyId]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // ── Fetch detail data when a pricelist is selected ────────────────────────

  const fetchItems = useCallback(async (plId: string) => {
    setItemsLoad(true); setItemsError(null);
    const { data, error } = await supabase
      .from('inventory_pricelist_items')
      .select(`
        id, pricelist_id, company_id, product_group_id, product_id, discount_percent,
        inventory_product_groups ( name ),
        inventory_products        ( name, sku )
      `)
      .eq('pricelist_id', plId)
      .order('discount_percent', { ascending: false });
    if (error) { setItemsError(error.message); setItemsLoad(false); return; }

    const mapped: PricelistItem[] = (data ?? []).map((r: any) => ({
      id:               r.id,
      pricelist_id:     r.pricelist_id,
      company_id:       r.company_id,
      product_group_id: r.product_group_id,
      product_id:       r.product_id,
      discount_percent: r.discount_percent,
      group_name:       r.inventory_product_groups?.name ?? null,
      product_name:     r.inventory_products?.name ?? null,
    }));
    setItems(mapped);
    setItemsLoad(false);
  }, []);

  const fetchGroups = useCallback(async () => {
    const { data } = await supabase
      .from('inventory_product_groups')
      .select('id, company_id, parent_id, name, use_as_b2b_filter, created_at, updated_at')
      .eq('company_id', companyId)
      .order('name');
    setGroups(data ?? []);
  }, [companyId]);

  const fetchProducts = useCallback(async () => {
    const { data } = await supabase
      .from('inventory_products')
      .select('id, name, sku')
      .eq('company_id', companyId)
      .order('name');
    setProducts(data ?? []);
  }, [companyId]);

  const openDetail = (pl: Pricelist) => {
    setSelected(pl);
    if (!pl.is_manual) {
      fetchItems(pl.id);
      fetchGroups();
      fetchProducts();
    }
  };

  // ── Pricelist CRUD ────────────────────────────────────────────────────────

  const openPlAdd = () => {
    setPlEditing(null);
    setPlName('');
    setPlDiscount('0');
    setPlIsManual(false);
    setPlFormErr(null);
    setPlModalOpen(true);
  };

  const openPlEdit = (pl: Pricelist) => {
    setPlEditing(pl);
    setPlName(pl.name);
    setPlDiscount(String(pl.default_discount_percent));
    setPlIsManual(pl.is_manual);
    setPlFormErr(null);
    setPlModalOpen(true);
  };

  const closePlModal = () => {
    setPlModalOpen(false);
    setPlEditing(null);
    setPlName('');
    setPlDiscount('0');
    setPlIsManual(false);
    setPlFormErr(null);
  };

  const validatePl = (): string | null => {
    if (!plName.trim()) return 'שם המחירון הוא שדה חובה';
    if (!plIsManual) {
      const d = parseFloat(plDiscount);
      if (isNaN(d) || d < 0 || d > 100) return 'הנחת ברירת מחדל חייבת להיות בין 0 ל-100';
    }
    return null;
  };

  const handlePlSave = async () => {
    const err = validatePl();
    if (err) { setPlFormErr(err); return; }
    setPlSaving(true); setPlFormErr(null);
    const payload = {
      name:                     plName.trim(),
      default_discount_percent: plIsManual ? 0 : parseFloat(plDiscount),
      is_manual:                plIsManual,
    };
    try {
      if (plEditing) {
        const { error } = await supabase
          .from('inventory_pricelists')
          .update(payload)
          .eq('id', plEditing.id);
        if (error) throw error;
        // Keep selected in sync
        if (selected?.id === plEditing.id) {
          setSelected(s => s ? { ...s, ...payload } : s);
        }
        showToast('מחירון עודכן בהצלחה');
      } else {
        const { error } = await supabase
          .from('inventory_pricelists')
          .insert({ company_id: companyId, ...payload });
        if (error) throw error;
        showToast('מחירון נוסף בהצלחה');
      }
      closePlModal();
      fetchLists();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'שגיאה';
      if (msg.toLowerCase().includes('unique')) setPlFormErr('שם מחירון זה כבר קיים');
      else showToast(msg, false);
    } finally {
      setPlSaving(false);
    }
  };

  const handlePlDelete = async () => {
    if (!plDelTarget) return;
    setPlDeleting(true);
    const { error } = await supabase
      .from('inventory_pricelists')
      .delete()
      .eq('id', plDelTarget.id);
    if (error) { showToast(error.message, false); }
    else {
      showToast('מחירון נמחק');
      if (selected?.id === plDelTarget.id) setSelected(null);
      setPlDelTarget(null);
      fetchLists();
    }
    setPlDeleting(false);
  };

  // ── Pricelist Items CRUD (percentage pricelists only) ─────────────────────

  const openItemAdd = () => {
    setItemEditing(null);
    setItemTarget('group');
    setItemGroupId('');
    setItemProductId('');
    setItemDiscount('0');
    setItemFormErr(null);
    setItemModalOpen(true);
  };

  const openItemEdit = (item: PricelistItem) => {
    setItemEditing(item);
    setItemTarget(item.product_group_id ? 'group' : 'product');
    setItemGroupId(item.product_group_id ?? '');
    setItemProductId(item.product_id ?? '');
    setItemDiscount(String(item.discount_percent));
    setItemFormErr(null);
    setItemModalOpen(true);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setItemEditing(null);
    setItemFormErr(null);
  };

  const validateItem = (): string | null => {
    if (itemTarget === 'group'   && !itemGroupId)   return 'בחר קבוצת מוצר';
    if (itemTarget === 'product' && !itemProductId) return 'בחר מוצר';
    const d = parseFloat(itemDiscount);
    if (isNaN(d) || d < 0 || d > 100) return 'ההנחה חייבת להיות בין 0 ל-100';
    return null;
  };

  const handleItemSave = async () => {
    const err = validateItem();
    if (err) { setItemFormErr(err); return; }
    if (!selected) return;
    setItemSaving(true); setItemFormErr(null);

    const payload = {
      pricelist_id:     selected.id,
      company_id:       companyId,
      product_group_id: itemTarget === 'group'   ? itemGroupId   : null,
      product_id:       itemTarget === 'product' ? itemProductId : null,
      discount_percent: parseFloat(itemDiscount),
    };

    try {
      if (itemEditing) {
        const { error } = await supabase
          .from('inventory_pricelist_items')
          .update({
            product_group_id: payload.product_group_id,
            product_id:       payload.product_id,
            discount_percent: payload.discount_percent,
          })
          .eq('id', itemEditing.id);
        if (error) throw error;
        showToast('כלל עודכן');
      } else {
        const { error } = await supabase
          .from('inventory_pricelist_items')
          .insert(payload);
        if (error) throw error;
        showToast('כלל נוסף');
      }
      closeItemModal();
      fetchItems(selected.id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'שגיאה';
      if (msg.toLowerCase().includes('unique')) {
        setItemFormErr('כבר קיים כלל עבור מטרה זו במחירון זה');
      } else if (msg.toLowerCase().includes('ipli_target_xor')) {
        setItemFormErr('חובה לבחור קבוצה או מוצר — לא שניהם');
      } else {
        showToast(msg, false);
      }
    } finally {
      setItemSaving(false);
    }
  };

  const handleItemDelete = async () => {
    if (!itemDelTarget || !selected) return;
    setItemDeleting(true);
    const { error } = await supabase
      .from('inventory_pricelist_items')
      .delete()
      .eq('id', itemDelTarget.id);
    if (error) { showToast(error.message, false); }
    else       { showToast('כלל נמחק'); setItemDelTarget(null); fetchItems(selected.id); }
    setItemDeleting(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Section Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <Tags className="w-5 h-5 text-violet-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">מחירונים</h2>
            <p className="text-xs text-gray-500">
              מחירוני הנחה (אחוזים) ומחירונים פרטניים (מחיר ידני למוצר)
            </p>
          </div>
        </div>
        <button
          onClick={openPlAdd}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          הוסף מחירון
        </button>
      </div>

      {/* Error */}
      {listsError && (
        <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {listsError}
        </div>
      )}

      {/* Two-column layout: Master | Detail */}
      <div className="flex min-h-[320px]">

        {/* ── Master list ───────────────────────────────────────────────────── */}
        <div className={`border-l border-gray-100 flex flex-col ${selected ? 'w-72 shrink-0' : 'flex-1'}`}>
          {listsLoad ? (
            <div className="flex items-center justify-center flex-1 gap-3 text-gray-400 py-16">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">טוען מחירונים…</span>
            </div>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-16 gap-3 text-gray-400">
              <Tags className="w-12 h-12 text-gray-200" />
              <p className="text-sm font-medium">אין מחירונים</p>
              <p className="text-xs text-gray-400">לחץ על "הוסף מחירון" כדי ליצור את הראשון</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {lists.map(pl => (
                <li
                  key={pl.id}
                  onClick={() => openDetail(pl)}
                  className={`flex items-center justify-between px-5 py-3.5 cursor-pointer transition-colors
                    ${selected?.id === pl.id
                      ? 'bg-violet-50 border-r-2 border-violet-500'
                      : 'hover:bg-gray-50'}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800 truncate">{pl.name}</p>
                      {/* Type badge */}
                      {pl.is_manual ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5
                                         bg-blue-50 text-blue-600 text-[10px] font-bold
                                         rounded border border-blue-200 shrink-0">
                          <SlidersHorizontal className="w-2.5 h-2.5" />
                          ידני
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5
                                         bg-violet-50 text-violet-600 text-[10px] font-bold
                                         rounded border border-violet-200 shrink-0">
                          <Percent className="w-2.5 h-2.5" />
                          הנחה
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      {pl.is_manual ? (
                        <>
                          <Package className="w-3 h-3" />
                          מחיר פרטני לכל מוצר
                        </>
                      ) : (
                        <>
                          <Percent className="w-3 h-3" />
                          הנחת בסיס: {fmtPct(pl.default_discount_percent)}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 mr-2">
                    <button
                      onClick={e => { e.stopPropagation(); openPlEdit(pl); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="ערוך מחירון"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setPlDelTarget(pl); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="מחק מחירון"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {selected?.id !== pl.id && (
                      <ChevronLeft className="w-4 h-4 text-gray-300" />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Detail panel ─────────────────────────────────────────────────── */}
        {selected && (
          <div className="flex-1 flex flex-col border-r border-gray-100 min-w-0">

            {/* Detail header */}
            <div className={`flex items-center justify-between px-5 py-3.5 border-b border-gray-100
                             ${selected.is_manual ? 'bg-blue-50/60' : 'bg-violet-50/60'}`}>
              <div className="min-w-0">
                <p className={`text-sm font-bold truncate ${selected.is_manual ? 'text-blue-800' : 'text-violet-800'}`}>
                  {selected.name}
                </p>
                <p className={`text-xs mt-0.5 ${selected.is_manual ? 'text-blue-600' : 'text-violet-600'}`}>
                  {selected.is_manual
                    ? 'מחירון פרטני — הגדר מחירים מתוך טופס המוצר'
                    : `הנחת בסיס: ${fmtPct(selected.default_discount_percent)} · ${items.length} כללים ספציפיים`
                  }
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!selected.is_manual && (
                  <button
                    onClick={openItemAdd}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    הוסף כלל
                  </button>
                )}
                <button
                  onClick={() => setSelected(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                  title="סגור פרטים"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── Manual pricelist: info card ─────────────────────────────── */}
            {selected.is_manual ? (
              <div className="flex flex-col items-center justify-center flex-1 py-14 px-8 gap-5 text-center">
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <SlidersHorizontal className="w-8 h-8 text-blue-500" />
                </div>
                <div className="space-y-2 max-w-xs">
                  <p className="text-sm font-semibold text-gray-800">מחירון פרטני / ידני</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    כדי להגדיר מחיר ספציפי למוצר במחירון זה,
                    פתח את עמוד <strong>קטלוג מוצרים</strong>, ערוך מוצר,
                    וגלול לסעיף <strong>"מחירונים פרטניים"</strong>.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                  <Info className="w-3.5 h-3.5 shrink-0" />
                  מחיר לא מוגדר = ברירת מחדל: מחיר הבסיס של המוצר
                </div>
              </div>
            ) : (
              <>
                {/* ── Percentage pricelist: rules table ───────────────────── */}

                {/* Detail error */}
                {itemsError && (
                  <div className="flex items-center gap-3 mx-5 my-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {itemsError}
                  </div>
                )}

                {/* Items */}
                {itemsLoad ? (
                  <div className="flex items-center justify-center flex-1 gap-3 text-gray-400 py-12">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">טוען כללים…</span>
                  </div>
                ) : items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center flex-1 py-12 gap-2 text-gray-400">
                    <ListFilter className="w-10 h-10 text-gray-200" />
                    <p className="text-sm font-medium">אין כללים ספציפיים</p>
                    <p className="text-xs text-gray-400">
                      כל המוצרים יקבלו הנחה של {fmtPct(selected.default_discount_percent)}
                    </p>
                    <button
                      onClick={openItemAdd}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-medium rounded-lg transition-colors border border-violet-200"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      הוסף כלל ספציפי
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto flex-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="px-5 py-3 text-right">סוג</th>
                          <th className="px-5 py-3 text-right">מטרה</th>
                          <th className="px-5 py-3 text-right">הנחה</th>
                          <th className="px-5 py-3 text-left w-24">פעולות</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {items.map(item => (
                          <tr key={item.id} className="hover:bg-gray-50/60 transition-colors group">
                            <td className="px-5 py-3">
                              {item.product_group_id ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                                  <Folder className="w-3 h-3" />
                                  קבוצה
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                                  <Package className="w-3 h-3" />
                                  מוצר
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-gray-800 font-medium">
                              {item.product_group_id
                                ? (item.group_name   ?? <span className="text-gray-400 text-xs">לא נמצא</span>)
                                : (item.product_name ?? <span className="text-gray-400 text-xs">לא נמצא</span>)}
                            </td>
                            <td className="px-5 py-3">
                              <span className="inline-flex items-center gap-1 font-semibold text-violet-700">
                                <Percent className="w-3.5 h-3.5" />
                                {fmtPct(item.discount_percent)}
                              </span>
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openItemEdit(item)}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="ערוך"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setItemDelTarget(item)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="מחק"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Priority legend */}
                {items.length > 0 && (
                  <div className="px-5 py-3 border-t border-gray-50 text-xs text-gray-400 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-violet-400" />
                    עדיפות: כלל מוצר ספציפי {'>'} כלל קבוצה {'>'} הנחת בסיס מחירון
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer count */}
      {!selected && lists.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
          {lists.length} מחירונים · לחץ על מחירון כדי לנהל את הכללים שלו
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Pricelist Add / Edit Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {plModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {plEditing ? 'עריכת מחירון' : 'הוספת מחירון חדש'}
              </h3>
              <button onClick={closePlModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {plFormErr && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {plFormErr}
              </div>
            )}

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם המחירון <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={plName}
                onChange={e => setPlName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePlSave()}
                placeholder="לדוגמה: לקוחות VIP, סיטונאי, קמעונאי…"
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
              />
            </div>

            {/* Pricelist type toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">סוג המחירון</label>
              <div className="grid grid-cols-2 gap-2">
                {/* Percentage type */}
                <button
                  type="button"
                  onClick={() => setPlIsManual(false)}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-sm
                               font-medium transition-all
                    ${!plIsManual
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}
                >
                  <Percent className={`w-5 h-5 ${!plIsManual ? 'text-violet-600' : 'text-gray-400'}`} />
                  <span className="text-xs leading-tight text-center">
                    הנחה קבועה<br />(אחוזים)
                  </span>
                </button>

                {/* Manual type */}
                <button
                  type="button"
                  onClick={() => setPlIsManual(true)}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-sm
                               font-medium transition-all
                    ${plIsManual
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}
                >
                  <SlidersHorizontal className={`w-5 h-5 ${plIsManual ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-xs leading-tight text-center">
                    מחירון פרטני<br />(ידני)
                  </span>
                </button>
              </div>

              {/* Description under type */}
              {plIsManual ? (
                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                  מחיר ספציפי לכל מוצר — מוגדר מתוך טופס המוצר.
                  אם לא הוגדר מחיר, יחול מחיר הבסיס של המוצר.
                </p>
              ) : (
                <p className="text-xs text-violet-600 bg-violet-50 px-3 py-2 rounded-lg border border-violet-100 leading-relaxed">
                  אחוז הנחה יחול על מחיר הבסיס של כל מוצר. ניתן להגדיר חריגים לפי קבוצה או מוצר ספציפי.
                  <br />
                  <span className="text-violet-500">
                    היברידי: ניתן גם לדרוס מחיר ספציפי לפריט בודד מתוך טופס המוצר.
                  </span>
                </p>
              )}
            </div>

            {/* Discount % — only shown for percentage pricelists */}
            {!plIsManual && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  הנחת ברירת מחדל (%)
                </label>
                <div className="relative">
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={plDiscount}
                    onChange={e => setPlDiscount(e.target.value)}
                    dir="ltr"
                    className="w-full pr-3 pl-9 py-2.5 border border-gray-300 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  הנחה זו תחול על כל מוצר שאין לו כלל ספציפי במחירון
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 justify-end">
              <button onClick={closePlModal}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handlePlSave}
                disabled={plSaving || !plName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {plSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {plEditing ? 'שמור שינויים' : 'הוסף מחירון'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricelist Delete Confirm */}
      {plDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת מחירון</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את המחירון{' '}
                  <span className="font-semibold text-gray-800">"{plDelTarget.name}"</span>?
                  {plDelTarget.is_manual
                    ? ' כל מחירי המוצרים שהוגדרו במחירון זה יימחקו גם כן.'
                    : ' כל הכללים הספציפיים שלו יימחקו גם כן.'
                  }
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setPlDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handlePlDelete}
                disabled={plDeleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {plDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Pricelist Item Add / Edit Modal  (percentage pricelists only)
          ══════════════════════════════════════════════════════════════════════ */}
      {itemModalOpen && selected && !selected.is_manual && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {itemEditing ? 'עריכת כלל הנחה' : 'הוספת כלל הנחה'}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  מחירון: <strong>{selected.name}</strong>
                </p>
              </div>
              <button onClick={closeItemModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {itemFormErr && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {itemFormErr}
              </div>
            )}

            {/* Target type toggle */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">סוג המטרה</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setItemTarget('group'); setItemProductId(''); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors
                    ${itemTarget === 'group'
                      ? 'bg-amber-50 border-amber-400 text-amber-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <Folder className="w-4 h-4" />
                  קבוצת מוצר
                </button>
                <button
                  type="button"
                  onClick={() => { setItemTarget('product'); setItemGroupId(''); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors
                    ${itemTarget === 'product'
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <Package className="w-4 h-4" />
                  מוצר ספציפי
                </button>
              </div>
            </div>

            {/* Group selector */}
            {itemTarget === 'group' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  קבוצת מוצר <span className="text-red-500">*</span>
                </label>
                <select
                  value={itemGroupId}
                  onChange={e => setItemGroupId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white
                             focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                >
                  <option value="">— בחר קבוצה —</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                {groups.length === 0 && (
                  <p className="text-xs text-amber-600">
                    לא הוגדרו קבוצות מוצרים. הגדר קבוצות בלשונית "קבוצות מוצרים".
                  </p>
                )}
              </div>
            )}

            {/* Product selector */}
            {itemTarget === 'product' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  מוצר ספציפי <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={products.map(p => ({ value: p.id, label: p.name, secondary: p.sku || undefined }))}
                  value={itemProductId}
                  onChange={setItemProductId}
                  placeholder="— בחר מוצר —"
                />
                {products.length === 0 && (
                  <p className="text-xs text-blue-600">
                    לא הוגדרו מוצרים עדיין.
                  </p>
                )}
              </div>
            )}

            {/* Discount % */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                הנחה (%) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={itemDiscount}
                  onChange={e => setItemDiscount(e.target.value)}
                  dir="ltr"
                  className="w-full pr-3 pl-9 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-400">
                כלל זה יגבר על הנחת ברירת המחדל ({fmtPct(selected.default_discount_percent)})
              </p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={closeItemModal}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handleItemSave}
                disabled={itemSaving}
                className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {itemSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {itemEditing ? 'שמור שינויים' : 'הוסף כלל'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Delete Confirm */}
      {itemDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת כלל הנחה</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הכלל עבור{' '}
                  <span className="font-semibold text-gray-800">
                    {itemDelTarget.group_name ?? itemDelTarget.product_name ?? '—'}
                  </span>?
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setItemDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handleItemDelete}
                disabled={itemDeleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {itemDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
